/// <amd-module name="Build/Common/Library/Security" />
import {
    Build,
    DefinitionReference
} from "Build/Client/Contracts/Build";
import { PermissionsStore } from 'Stores/Permissions';

export namespace Constants {
    export const BuildNamespaceId = "33344d9c-fc72-4d6f-aba5-fa317101a7e9";
    export const DteSecurityNamespaceId: string = "101eae8c-1709-47f9-b228-0e476c35b3ba";
    export const AgentPoolToken: string = "AgentPools";
}

export namespace AgentPoolPermissions {
    export const Manage: number = 8;
}

export namespace BuildRequiredPermissions {
    export const ViewBuilds = 1;
    export const EditBuildQuality = 2;
    export const RetainIndefinitely = 4;
    export const DeleteBuilds = 8;
    export const ManageBuildQualities = 16;
    export const DestroyBuilds = 32;
    export const UpdateBuildInformation = 64;
    export const QueueBuilds = 128;
    export const ManageBuildQueue = 256;
    export const StopBuilds = 512;
    export const ViewBuildDefinition = 1024;
    export const EditBuildDefinition = 2048;
    export const DeleteBuildDefinition = 4096;
    export const OverrideBuildCheckInValidation = 8192;
    export const AdministerBuildPermissions = 16384;
    export const EditPipelineQueueConfigurationPermission = 65536;
    export const AllPermissions = 98303;
}

export namespace UserClaims {
    /**
     * User is not authenticated.
     */
    export const Anonymous = "anonymous";

    /**
     * User carrying this claim is not a member of the current project but a member of the current account.
     */
    export const Public = "public";

    /**
     * User carrying this claim is a member of the current project.
     */
    export const Member = "member";
}

export interface ISecurityOptions {
    store: PermissionsStore;
    projectId: string;
}

const s_separator = "/";
const s_rootPath = "\\";

export class Security {
    private _store: PermissionsStore;
    private _projectId: string;

    constructor(options: ISecurityOptions) {
        this._store = options.store;
        this._projectId = options.projectId;
    }

    public canEditDefinition(definition: DefinitionReference): boolean {
        return this.hasDefinitionPermission(definition, BuildRequiredPermissions.EditBuildDefinition);
    }

    public canChangeBuild(definition: DefinitionReference): boolean {
        return this.hasDefinitionPermission(definition, BuildRequiredPermissions.EditBuildQuality);
    }

    public canEditPipelineQueueConfiguration(definition: DefinitionReference): boolean {
        return this.hasDefinitionPermission(definition, BuildRequiredPermissions.EditPipelineQueueConfigurationPermission);
    }

    public canCancelBuild(userId: string, build: Build): boolean {
        const requestedBy = (build.requestedBy && build.requestedBy.id || "").toLowerCase();
        const requestedFor = (build.requestedFor && build.requestedFor.id || "").toLowerCase();
        userId = userId || userId.toLowerCase();

        // cancellation is enabled if the user matches requestedBy or requestedFor, or has StopBuilds on the definition
        return userId === requestedBy ||
            userId === requestedFor ||
            this.hasDefinitionPermission(build.definition, BuildRequiredPermissions.StopBuilds);
    }

    public canCancelBuildByUserId(userId: string, definitionId: string, definitionPath: string, requestedForId: string, requestedById: string): boolean {
        const requestedBy = (requestedById || "").toLowerCase();
        const requestedFor = (requestedForId || "").toLowerCase();
        userId = userId || userId.toLowerCase();

        // cancellation is enabled if the user matches requestedBy or requestedFor, or has StopBuilds on the definition
        return userId === requestedBy ||
            userId === requestedFor ||
            this._hasDefinitionPermission(definitionId, definitionPath, BuildRequiredPermissions.StopBuilds);
    }

    public canDeleteBuild(definition: DefinitionReference): boolean {
        return this.hasDefinitionPermission(definition, BuildRequiredPermissions.DeleteBuilds);
    }

    public canDeleteBuildByDefinitionId(definitionId: string, definitionPath: string): boolean {
        return this._hasDefinitionPermission(definitionId, definitionPath, BuildRequiredPermissions.DeleteBuilds);
    }

    public canQueueBuild(definition: DefinitionReference): boolean {
        return this.hasDefinitionPermission(definition, BuildRequiredPermissions.QueueBuilds);
    }

    public canQueueBuildById(definitionId: string, definitionPath: string): boolean {
        return this._hasDefinitionPermission(definitionId, definitionPath, BuildRequiredPermissions.QueueBuilds);
    }

    public canEditTagsByUserId(userId: string, definitionId: string, definitionPath: string, requestedForId: string, requestedById: string): boolean {
        const requestedBy = (requestedById || "").toLowerCase();
        const requestedFor = (requestedForId || "").toLowerCase();
        userId = userId || userId.toLowerCase();

        return userId === requestedBy ||
            userId === requestedFor ||
            this._hasDefinitionPermission(definitionId, definitionPath, BuildRequiredPermissions.EditBuildQuality);
    }

    public canRetainBuild(definition: DefinitionReference): boolean {
        return this.hasDefinitionPermission(definition, BuildRequiredPermissions.RetainIndefinitely) || this.hasDefinitionPermission(definition, BuildRequiredPermissions.UpdateBuildInformation);
    }

    public canRetainBuildByDefinitionId(definitionId: string, definitionPath: string): boolean {
        return this._hasDefinitionPermission(definitionId, definitionPath, BuildRequiredPermissions.RetainIndefinitely) || this._hasDefinitionPermission(definitionId, definitionPath, BuildRequiredPermissions.UpdateBuildInformation);
    }

    public hasDefinitionPermission(definition: DefinitionReference, permissions: number): boolean {
        return this._store.hasPermission(Constants.BuildNamespaceId, Security.getDefinitionSecurityToken(this._projectId, definition), permissions);
    }

    public hasFolderPermission(path: string, permissions: number): boolean {
        return this._store.hasPermission(Constants.BuildNamespaceId, Security._getDefinitionFolderSecurityToken(this._projectId, path), permissions);
    }

    public isAnonymousUser() {
        return this._store.hasClaim(UserClaims.Anonymous);
    }

    public isPublicUser() {
        return this._store.hasClaim(UserClaims.Public);
    }

    public isMember() {
        return this._store.hasClaim(UserClaims.Member);
    }

    // DT
    public hasManageAgentPoolPermission(poolId: number): boolean {
        return this._store.hasPermission(Constants.DteSecurityNamespaceId, Security._getAgentPoolSecurityToken(poolId), AgentPoolPermissions.Manage);
    }

    public static getDefinitionSecurityToken(projectId: string, definition: DefinitionReference) {
       return Security._getDefinitionSecurityToken(projectId, definition.id.toString(), definition.path )
    }

    public static getSecurityTokenPath(path: string): string {
        path = path.replace(/\\/g, s_separator);
        if (path[0] === s_separator) {
            // unroot the path
            path = path.slice(1, path.length);
        }

        return path;
    }

    private _hasDefinitionPermission(definitionId: string, definitionPath: string, permissions: number): boolean {
        return this._store.hasPermission(Constants.BuildNamespaceId, Security._getDefinitionSecurityToken(this._projectId, definitionId, definitionPath), permissions);
    }

    private static _getDefinitionSecurityToken(projectId: string, definitionId: string, definitionPath: string) {
        let token = definitionId;
        if (definitionPath !== s_rootPath) {
            token = Security.getSecurityTokenPath(definitionPath) + s_separator + token;
        }

        return projectId + s_separator + token;
    }

    private static _getDefinitionFolderSecurityToken(projectId: string, folderPath: string) {
        if (folderPath === s_rootPath) {
            return projectId;
        }
        return projectId + s_separator + Security.getSecurityTokenPath(folderPath) + s_separator;
    }

    private static _getAgentPoolSecurityToken(poolId: number | undefined): string {
        let token: string = Constants.AgentPoolToken;

        if (poolId) {
            token = token.concat(s_separator, poolId.toString());
        }

        return token;
    }
}
